import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Users, Check, X, Trophy, Megaphone, Gift } from "lucide-react";

export default function PlayJoin() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(params.get("code") || "");
  const [name, setName] = useState(user?.name || "");
  const [me, setMe] = useState(null); // { player_id, code, name }
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [sponsored, setSponsored] = useState([]);
  const pollRef = useRef(null);
  const lastQRef = useRef(-1);

  useEffect(() => {
    // Route to Maestro player when the QR sets ?maestro=1
    if (params.get("maestro") === "1") {
      const qs = params.get("code") ? `?code=${params.get("code")}` : "";
      navigate(`/play/maestro/play${qs}`, { replace: true });
    }
  }, [params, navigate]);

  useEffect(() => {
    // Surface sponsored games on the lobby screen
    api.get("/games/sponsored").then((r) => setSponsored(r.data || [])).catch(() => setSponsored([]));
  }, []);

  useEffect(() => {
    if (!me) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tlp/play/room/${me.code}?player_id=${me.player_id}`);
        setState(data);
        if (data.current_idx !== lastQRef.current) {
          lastQRef.current = data.current_idx;
          setSelected(null);
          setFeedback(null);
        }
        if (data.status === "ended" && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [me?.code]); // eslint-disable-line

  const join = async () => {
    if (!code || !name) { toast.error("Code + pseudo requis"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/play/join", { code: code.replace(/\s/g, ""), player_name: name });
      setMe(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  const answer = async (idx) => {
    if (selected != null || !state?.current_question) return;
    setSelected(idx);
    try {
      const { data } = await api.post("/tlp/play/answer", {
        code: me.code, player_id: me.player_id, question_idx: state.current_idx, answer_idx: idx,
      });
      setFeedback(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setSelected(null);
    }
  };

  if (!me) {
    return (
      <div className="max-w-md mx-auto px-5 md:px-10 py-16" data-testid="play-join">
        <span className="chip-pink">Play · Join</span>
        <h1 className="font-display text-4xl text-[#1F3A5F] mt-4 leading-[1.05]">Rejoindre une partie</h1>
        <p className="text-[#4A5568] mt-3">Saisissez le code à 6 chiffres de l'hôte.</p>
        <div className="mt-8 app-card p-6 space-y-5">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Code</span>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="join-code"
              maxLength={6} placeholder="123456"
              className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-3 text-3xl tracking-widest text-center font-display text-[#1F3A5F]" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Pseudo</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="join-name"
              maxLength={30} placeholder="geoVINUM AI, Romain, …"
              className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]" />
          </label>
          <button onClick={join} disabled={busy} data-testid="join-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Rejoindre
          </button>
          <Link to="/play/hosted" className="block text-center text-xs text-[#8B95A1] hover:text-[#E5276A] underline">Héberger ma propre partie</Link>
        </div>

        {/* Sponsored games */}
        {sponsored.length > 0 && (
          <div className="mt-10" data-testid="join-sponsored">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-4 h-4 text-[#E5276A]" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">Jeux sponsorisés</div>
            </div>
            <div className="space-y-2">
              {sponsored.slice(0, 5).map((s) => (
                <Link key={s.id || s.game_id} to={`/play?sponsored=${s.id || s.game_id}`} data-testid={`join-sponsored-${s.id || s.game_id}`}
                  className="app-card p-4 flex items-center gap-3 hover:border-[#E5276A] group">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FCE4ED] to-[#E5276A]/20 flex items-center justify-center shrink-0">
                    <Gift className="w-5 h-5 text-[#E5276A]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-[#1F3A5F] text-sm truncate">{s.title || s.type || "Quiz sponsorisé"}</h3>
                    {s.reward && <p className="text-xs text-[#8B95A1]">Récompense : {typeof s.reward === "string" ? s.reward : s.reward?.label || JSON.stringify(s.reward)}</p>}
                    {s.sponsor && <p className="text-[10px] text-[#8B95A1] mt-0.5">Sponsorisé par {s.sponsor}</p>}
                  </div>
                  <span className="text-xs font-bold text-[#E5276A] group-hover:underline shrink-0">Jouer →</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const cq = state?.current_question;
  const isLobby = state?.status === "lobby";
  const ended = state?.status === "ended";
  const myScore = state?.scoreboard?.find((s) => s.player_id === me.player_id);

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="play-join-room">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{state?.theme || "—"}</span>
          <h2 className="font-display text-2xl text-[#1F3A5F]">Partie {me.code}</h2>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F3A5F] text-white text-xs font-bold">
          <Users className="w-3.5 h-3.5" /> {state?.players?.length || 0}
        </span>
      </div>

      {isLobby && (
        <div className="app-card p-8 text-center" data-testid="join-lobby">
          <Loader2 className="w-8 h-8 text-[#E5276A] animate-spin mx-auto" />
          <p className="mt-4 text-[#4A5568]">En attente du démarrage par l'hôte…</p>
          <p className="mt-1 text-xs text-[#8B95A1]">{state?.players?.length || 0} joueur(s) en lobby</p>
        </div>
      )}

      {cq && !ended && (
        <div className="app-card p-6" data-testid="join-current-question">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">Question {cq.idx + 1} / {state.n_questions}</div>
          <h3 className="font-display text-2xl text-[#1F3A5F]">{cq.question}</h3>
          <div className="grid gap-2 mt-5">
            {cq.choices.map((c, i) => {
              const isSelected = selected === i;
              const reveal = feedback != null;
              const isCorrect = reveal && feedback.correct_idx === i;
              const isWrong = reveal && isSelected && !feedback.correct;
              return (
                <button key={i} onClick={() => answer(i)} disabled={selected != null} data-testid={`join-choice-${i}`}
                  className={`text-left p-4 rounded-xl border-2 text-sm transition-all flex items-center gap-3 ${
                    isCorrect ? "bg-green-50 border-green-500 text-green-900 font-bold" :
                    isWrong ? "bg-red-50 border-red-500 text-red-900" :
                    isSelected ? "bg-[#E5276A] text-white border-[#E5276A]" :
                    "bg-white border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
                  } disabled:opacity-100`}
                >
                  <span className="font-bold opacity-70">{String.fromCharCode(65 + i)}.</span>
                  <span className="flex-1">{c}</span>
                  {isCorrect && <Check className="w-4 h-4" />}
                  {isWrong && <X className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          {feedback && (
            <div className={`mt-4 p-3 rounded-xl text-sm ${feedback.correct ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}>
              <b>{feedback.correct ? "Bravo 🎉" : "Pas tout à fait."}</b> {feedback.why}
              <p className="text-xs mt-1 opacity-70">En attente de la question suivante…</p>
            </div>
          )}
        </div>
      )}

      {ended && (
        <div className="app-card p-7 text-center" data-testid="join-ended">
          <Trophy className="w-10 h-10 text-[#E5276A] mx-auto" />
          <h2 className="font-display text-3xl text-[#1F3A5F] mt-3">Partie terminée</h2>
          <p className="text-base text-[#4A5568] mt-2">Votre score : <b className="text-[#1F3A5F]">{myScore?.score || 0} / {state.n_questions}</b></p>
          {state.scoreboard[0] && (
            <p className="text-sm text-[#8B95A1] mt-1">🏆 {state.scoreboard[0].name} ({state.scoreboard[0].score}/{state.n_questions})</p>
          )}
        </div>
      )}

      {state?.scoreboard?.length > 0 && (
        <div className="mt-5 app-card p-4" data-testid="join-scoreboard">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#E5276A] mb-2">Classement</div>
          <ul className="space-y-1">
            {state.scoreboard.map((s, i) => (
              <li key={s.player_id} className={`flex items-center justify-between p-2 rounded-lg ${s.player_id === me.player_id ? "bg-[#FCE4ED]" : ""}`}>
                <span className="text-sm text-[#1F3A5F] font-bold">{i + 1}. {s.name} {s.player_id === me.player_id && "(vous)"}</span>
                <span className="text-xs text-[#4A5568]">{s.score} / {state.n_questions}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
